import { useCallback, useEffect, useRef, useState } from 'react';

import { useLazyGetDocumentProxyBlobQuery } from './api';

interface UseDocumentBlobUrlOptions {
  disposition?: 'inline' | 'attachment';
}

interface UseDocumentBlobUrlResult {
  blobUrl: string | null;
  isLoading: boolean;
  error: Error | null;
  fetchDocument: (documentId: string) => Promise<string | null>;
  clearBlobUrl: () => void;
}

/**
 * Hook to fetch a document via proxy and manage its blob URL lifecycle.
 * Automatically revokes blob URLs on cleanup to prevent memory leaks.
 */
export const useDocumentBlobUrl = (
  options: UseDocumentBlobUrlOptions = {},
): UseDocumentBlobUrlResult => {
  const { disposition = 'inline' } = options;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const [triggerFetch, { isLoading }] = useLazyGetDocumentProxyBlobQuery();

  // Cleanup blob URL on unmount or when creating a new one
  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const clearBlobUrl = useCallback(() => {
    revokeBlobUrl();
    setBlobUrl(null);
    setError(null);
  }, [revokeBlobUrl]);

  const fetchDocument = useCallback(
    async (documentId: string): Promise<string | null> => {
      try {
        setError(null);
        revokeBlobUrl(); // Clean up previous blob URL

        const blob = await triggerFetch({ documentId, disposition }).unwrap();
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
        return url;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to fetch document');
        setError(error);
        return null;
      }
    },
    [triggerFetch, disposition, revokeBlobUrl],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      revokeBlobUrl();
    };
  }, [revokeBlobUrl]);

  return {
    blobUrl,
    isLoading,
    error,
    fetchDocument,
    clearBlobUrl,
  };
};
